import {Component, OnInit} from '@angular/core';
import {async, BehaviorSubject, combineLatest, empty, Observable, of, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {sortBy} from 'lodash-es';

import {
  LicenseType,
  Product,
  ProductOffering,
  ProductService,
  Status,
  TitleSortService,
  UserService
} from '@uofu-uss-aux/software-catalog-ng-core';
import {NotificationService} from '@uofu-uss/angular-util';

import {
  FilterChoice,
  ProductFilterChooserComponent
} from './product-filter/product-filter-chooser/product-filter-chooser.component';
import {ProductFilterService} from './product-filter/product-filter.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {ProductComponent} from "../product/product.component";
import {EmptyPipe} from "./empty-pipe/empty.pipe";

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    ProductFilterChooserComponent,
    ProductComponent,
    NgForOf,
    AsyncPipe,
    EmptyPipe
  ]
})

export class ProductsComponent implements OnInit {

  /* Filters from the selection (LicenseType, Platform) */
  filters$: Subject<FilterChoice> = new BehaviorSubject({});

  /* All the products available to the user.
   * Products without licenses or offerings aren't included */
  products$: Observable<ReadonlyArray<Product>>;

  /* The products after being filtered by the filter-selection and
   * filtered by whether we're only showing featured products */
  productsToDisplay$: Observable<ReadonlyArray<Product>>;

  /* Whether the 'See The Full List Of Products' button has been pressed */
  displayAllPressed$: Subject<boolean> = new BehaviorSubject(false);

  /* Only employees are shown the featured product list */
  isShowingOnlyFeatured$: Observable<boolean> = this.displayAllPressed$
    .pipe(map(displayAll => !displayAll && this.userIsEmployee()));

  constructor(private readonly productService: ProductService,
              private readonly titleSortService: TitleSortService,
              private readonly notificationService: NotificationService,
              private readonly userService: UserService,
              private readonly router: Router,
              private readonly route: ActivatedRoute) {
  }

  /** Filters out products have no licenses */
  static filterEmptyProducts(products: readonly Product[]): readonly Product[] {
    return products
      .map(ProductsComponent.filterEmptyLicenses)
      .filter(p => p.licenses.length > 0);
  }

  /** Filters out licenses that have no offerings */
  static filterEmptyLicenses({licenses, ...product}: Product) {
    return {
      licenses: licenses.filter(l => l.offerings.length > 0),
      ...product
    };
  }

  ngOnInit(): void {
    this.products$ = this.fetchProducts();
    this.route
      .queryParamMap
      .subscribe((paramMap) => {
        if (paramMap.get('view') === 'all') {
          this.displayAllProducts();
        }
      });

    // Filter for only featured products as long as we are showing only them
    const featuredProducts$ =
      combineLatest([this.products$, this.isShowingOnlyFeatured$])
        .pipe(
          map(([products, isShowingOnlyFeatured]) =>
            isShowingOnlyFeatured
              ? sortBy(products.filter(p => p.featured), ['title'])
              : sortBy(products, ['title'])));

    // Apply the filters from the filter-selector
    this.productsToDisplay$ =
      combineLatest([featuredProducts$, this.filters$])
        .pipe(
          map(([products, filterChoice]) =>
            ProductFilterService.filterOnChoice(products, filterChoice)));
  }

  fetchProducts(): Observable<readonly Product[]> {
    const productFilters = {
      /* Employees can see all license types---including student in case a faculty
       * member wants to see what their students have access to. */
      licenseTypes: this.userIsEmployee()
        ? [LicenseType.Employee, LicenseType.University, LicenseType.Student]
        : [LicenseType.Student],

      productStatus: Status.Active,
      licenseStatus: Status.Active,
      offeringStatus: Status.Active
    };

    return this.productService.fetchProducts(productFilters)
      .pipe(
        catchError(_ => {
          this.notificationService.addError('Unable to load products. Please contact support if the problem continues.');
          return of([]);
        }),
        // Remove products which don't have offerings or licenses
        map(ProductsComponent.filterEmptyProducts));
  }

  userIsEmployee(): boolean {
    return this.userService.value.employee;
  }

  selectNewFilters(filterChoice: FilterChoice) {
    this.filters$.next(filterChoice);
  }

  /** sets whether to show only the featured products or all the products */
  displayAllProducts() {
    this.displayAllPressed$.next(true);
  }

  getStudentOfferings(product: Product): ReadonlyArray<ProductOffering> {
    const license = product?.licenses && this.productService.findLicense(product, LicenseType.Student);
    return license?.offerings;
  }

  getEmployeeOfferings(product: Product): ReadonlyArray<ProductOffering> {
    const license = product?.licenses && this.productService.findLicense(product, LicenseType.Employee);
    return license?.offerings;
  }

  getUniversityOfferings(product: Product): ReadonlyArray<ProductOffering> {
    const license = product?.licenses && this.productService.findLicense(product, LicenseType.University);
    return license?.offerings;
  }

}
